import {
  adminStorefrontSetupModifyAccessControl,
  partnerStorefrontSetupModifyAccessControl,
} from '../access-control/onboardingAccess.configuration'
import { type StoreHours } from '../api'
import { type ApprovalRequestData } from '../components/utils/Onboarding.types'
import { useDomainAccessControl } from './domain/accessControl'
import { useDomainMessages } from './domain/intl'

/** Utils for Store Hour **/
export const isStoreHourCompleted = (storeHours: StoreHours) =>
  // store hour is considered completed when regular hours is not null
  Boolean(storeHours?.regularHours)

/** Check if a warehouse location has completed store hour from a bulk store hour query **/
export const isStoreHourCompletedForWarehouseLocation = (
  allStoreHours: StoreHours[],
  warehouseLocationId
) =>
  Boolean(allStoreHours) &&
  allStoreHours.some(
    storeHours =>
      storeHours?.retailerLocationId == warehouseLocationId && isStoreHourCompleted(storeHours)
  )

export const getLatestApprovalRequestUpdatedAt = (approvalRequests?: ApprovalRequestData[]) => {
  if (!approvalRequests) return ''

  const sortedRequests = [...approvalRequests].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
  return sortedRequests[0]?.updatedAt
}

interface formatHoursAgoDateProps {
  date: Date | string
}

export const formatDate = (date: string) => {
  if (!date) return null
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export const useFormatHoursAgoDate = ({ date }: formatHoursAgoDateProps) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  const now = new Date()

  let diffHours = 0
  let formattedDate = ''
  let isSameDay = false

  if (date) {
    diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    isSameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
  }

  const messages = useDomainMessages(
    {
      updatedOn: 'onboardingDomain.common.formatHoursAgoDate.updatedOn',
      updatedLessThanOneHourAgo:
        'onboardingDomain.common.formatHoursAgoDate.updatedLessThanOneHourAgo',
      updatedHoursAgo: 'onboardingDomain.common.formatHoursAgoDate.updatedHoursAgo',
    },
    {
      date: formattedDate,
      hours: diffHours,
    }
  )

  if (!date) {
    return ''
  }

  if (diffHours < 1 && isSameDay) {
    return messages.updatedLessThanOneHourAgo
  }

  if (isSameDay) {
    return messages.updatedHoursAgo
  }

  return messages.updatedOn
}

interface useHasModifyAccess {
  isModifyingAdmin: boolean
}

export const useHasModifyAccess = ({ isModifyingAdmin }: useHasModifyAccess) => {
  const hasAccess = useDomainAccessControl()

  return isModifyingAdmin
    ? hasAccess(adminStorefrontSetupModifyAccessControl)
    : hasAccess(partnerStorefrontSetupModifyAccessControl)
}

interface useFormatOnboardingAttributeQuestionProps {
  question: string
  renderOptionalLabel: boolean
}

export const useFormatOnboardingAttributeQuestion = ({
  question,
  renderOptionalLabel,
}: useFormatOnboardingAttributeQuestionProps) => {
  const messages = useDomainMessages(
    {
      optionalQuestion: 'onboardingDomain.common.formatOnboardingAttributeQuestion.optional',
    },
    {
      question,
    }
  )

  return renderOptionalLabel ? messages.optionalQuestion : question
}
